import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  formatDate,
  formatDuration,
  formatPercent,
  formatPrice,
  formatPromptEntryPrice,
  formatSignedPercent,
  formatStatusLabel,
  getClosedExitValue,
  getClosedTradeOutcome,
  getLatestEventKind,
  getNormalizedStatus,
  getPendingActivationCondition,
  getProposalPercentChange,
  getTradeEventLabel,
  inferTradeSide,
  isRecentTradeEvent,
  toNonEmptyString,
} from "./tradeProposalPanelModel";

export default function TradeProposalAccordion({
  proposal,
  highlightChatMessageId,
  tradePanelColors,
  userTimezone,
  onQuestionSubmit,
  setActivePanel,
  trackingProposalId,
  onStopTrackingTrade,
}) {
  const isHighlighted =
    highlightChatMessageId != null &&
    String(proposal.chatMessageId) === highlightChatMessageId;
  const normalizedStatus = getNormalizedStatus(proposal);
  const isPendingTrade = normalizedStatus === "pending_activation";
  const isClosedTrade = normalizedStatus === "closed";
  const closedTradeOutcome = getClosedTradeOutcome(proposal);
  const cardToneSx = closedTradeOutcome === "win"
    ? {
        backgroundColor: tradePanelColors.completedWinBg,
        borderColor: tradePanelColors.completedWinBorder,
      }
    : closedTradeOutcome === "loss"
      ? {
          backgroundColor: tradePanelColors.completedLossBg,
          borderColor: tradePanelColors.completedLossBorder,
        }
      : null;
  const tickerLabel =
    proposal.ticker ||
    proposal.name ||
    `Ticker ${proposal.allDataTickerId}`;
  const tradeSide = inferTradeSide(proposal);
  const entry = formatPrice(proposal.entryPrice, proposal.assetType);
  const stopLoss = formatPrice(proposal.stopLoss, proposal.assetType);
  const priceTarget = formatPrice(
    proposal.priceTarget,
    proposal.assetType,
  );
  const duration = formatDuration(proposal.expectedTradeDuration);
  const current = formatPrice(
    proposal.currentPrice,
    proposal.assetType,
  );
  const proposedAt = formatPrice(
    proposal.livePriceAtSuggested,
    proposal.assetType,
  );
  const pnlPercent = getProposalPercentChange(proposal);
  const changeFromEntry = formatPercent(pnlPercent);
  const changeFromEntrySummary = formatSignedPercent(pnlPercent);
  const closedExit = isClosedTrade
    ? getClosedExitValue(proposal, proposal.assetType)
    : null;
  const pendingCondition = isPendingTrade
    ? getPendingActivationCondition(proposal, proposal.assetType)
    : null;
  const statusLabel = formatStatusLabel(normalizedStatus);
  const summaryChangeColor = pnlPercent == null
    ? tradePanelColors.subtext
    : pnlPercent > 0
      ? tradePanelColors.changeProfit
      : pnlPercent < 0
        ? tradePanelColors.changeLoss
        : tradePanelColors.subtext;
  const summaryParts = isClosedTrade
    ? [
        { value: `Exit: ${closedExit || "—"}` },
        { value: `Entry: ${entry || "—"}` },
        { value: `P&L: ${changeFromEntrySummary || "—"}`, isChange: true },
      ]
    : isPendingTrade
      ? [
          { value: `Current: ${current || "—"}` },
          { value: `Entry: ${entry || "—"}` },
          { value: `${pendingCondition || "Entry touch"}` },
        ]
      : [
          { value: `Current: ${current || "—"}` },
          { value: `Entry: ${entry || "—"}` },
          { value: `P&L: ${changeFromEntrySummary || "—"}`, isChange: true },
        ];
  const rewardRisk = proposal.rewardRiskRatio
    ? proposal.rewardRiskRatio.toFixed(2)
    : null;
  const createdAt = formatDate(proposal.createdAt, userTimezone);
  const notes = toNonEmptyString(proposal.notes);
  const latestEventKind = getLatestEventKind(proposal);
  const latestEventLabel = getTradeEventLabel(latestEventKind);
  const hasRecentEvent = isRecentTradeEvent(proposal);
  const eventToneSx = hasRecentEvent
    ? latestEventKind === "profit_target_hit"
      ? {
          backgroundColor: tradePanelColors.completedWinBg,
          borderColor: tradePanelColors.completedWinBorder,
          color: tradePanelColors.changeProfit,
        }
      : latestEventKind === "stop_loss_hit"
        ? {
            backgroundColor: tradePanelColors.completedLossBg,
            borderColor: tradePanelColors.completedLossBorder,
            color: tradePanelColors.changeLoss,
          }
        : {
            backgroundColor: tradePanelColors.highlightBg,
            borderColor: tradePanelColors.highlightBorder,
            color: tradePanelColors.text,
          }
    : null;

  const detailItems = isPendingTrade
    ? [
        { label: "Awaiting", value: pendingCondition },
        { label: "Entry", value: entry },
        { label: "Stop", value: stopLoss },
        { label: "Target", value: priceTarget },
        { label: "Duration", value: duration },
        { label: "Current", value: current },
        { label: "At Proposal", value: proposedAt },
        { label: "R/R", value: rewardRisk },
      ]
    : [
        { label: "Entry", value: entry },
        { label: "Stop", value: stopLoss },
        { label: "Target", value: priceTarget },
        { label: "Duration", value: duration },
        { label: "Current", value: current },
        { label: "At Proposal", value: proposedAt },
        { label: "P&L", value: changeFromEntry },
        { label: "R/R", value: rewardRisk },
      ];
  const tickerId = Number(proposal.allDataTickerId);
  const canAnalyze =
    typeof onQuestionSubmit === "function" &&
    proposal?.id != null &&
    Number.isFinite(tickerId);
  const isStoppingTracking = trackingProposalId === proposal.id;

  return (
    <Accordion
      disableGutters
      square
      data-chat-message-id={proposal.chatMessageId ?? ""}
      sx={{
        backgroundColor:
          eventToneSx?.backgroundColor ||
          cardToneSx?.backgroundColor ||
          "transparent",
        border: `1px solid ${
          eventToneSx?.borderColor ||
          cardToneSx?.borderColor ||
          tradePanelColors.divider
        }`,
        borderRadius: 1,
        transition:
          "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
        ...(isHighlighted
          ? {
              borderColor: tradePanelColors.highlightBorder,
              backgroundColor: tradePanelColors.highlightBg,
              boxShadow: tradePanelColors.highlightShadow,
            }
          : {}),
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: tradePanelColors.subtext }} />
        }
        sx={{
          px: 1,
          py: 0.5,
          minHeight: "unset",
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
          "&:hover": {
            backgroundColor: tradePanelColors.itemHover,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 0.75,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: tradePanelColors.text,
                  fontWeight: 600,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {tickerLabel}
              </Typography>
              {tradeSide && (
                <Typography
                  variant="caption"
                  sx={{
                    color: tradePanelColors.subtext,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                    textTransform: "lowercase",
                  }}
                >
                  {tradeSide}
                </Typography>
              )}
              {statusLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    color: isPendingTrade
                      ? tradePanelColors.pendingText
                      : tradePanelColors.subtext,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {statusLabel}
                </Typography>
              )}
              {hasRecentEvent && latestEventLabel && (
                <Chip
                  size="small"
                  label={latestEventLabel}
                  sx={{
                    height: 20,
                    borderRadius: "999px",
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: eventToneSx?.color || tradePanelColors.text,
                    border: `1px solid ${eventToneSx?.borderColor || tradePanelColors.divider}`,
                    backgroundColor: eventToneSx?.backgroundColor || tradePanelColors.itemHover,
                    "& .MuiChip-label": {
                      px: 0.9,
                    },
                  }}
                />
              )}
            </Box>
            {createdAt && (
              <Typography
                variant="caption"
                sx={{
                  color: tradePanelColors.subtext,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {createdAt}
              </Typography>
            )}
          </Box>
          {summaryParts.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto auto 1fr",
                alignItems: "center",
                columnGap: 1.75,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {summaryParts.map((part, index) => (
                <Typography
                  key={`${proposal.id}-summary-${index}`}
                  variant="caption"
                  sx={{
                    color: part.isChange
                      ? summaryChangeColor
                      : tradePanelColors.subtext,
                    fontWeight: part.isChange ? 600 : 400,
                    whiteSpace: "nowrap",
                    ...(index === 2 && {
                      justifySelf: "end",
                      textAlign: "right",
                    }),
                  }}
                >
                  {part.value}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 0.75,
          }}
        >
          {detailItems.map((item) => (
            <Box
              key={item.label}
              sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: tradePanelColors.subtext,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontSize: "0.6rem",
                  display: "inline-block",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: tradePanelColors.text }}
              >
                {item.value || "—"}
              </Typography>
            </Box>
          ))}
        </Box>
        {notes && (
          <Box sx={{ mt: 0.75 }}>
            <Typography
              variant="caption"
              sx={{
                color: tradePanelColors.subtext,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontSize: "0.6rem",
              }}
            >
              Notes
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: tradePanelColors.text,
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {notes}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          <Box
            className="base-text"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 0.6,
              borderRadius: "999px",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: tradePanelColors.text,
              backgroundColor: tradePanelColors.itemHover,
              border: `1px solid ${tradePanelColors.divider}`,
              cursor: canAnalyze ? "pointer" : "default",
              userSelect: "none",
              "&:hover": canAnalyze
                ? {
                    borderColor: tradePanelColors.highlightBorder,
                    backgroundColor: tradePanelColors.highlightBg,
                  }
                : {},
            }}
            onClick={() => {
              if (!canAnalyze) return;
              const labelTicker =
                toNonEmptyString(proposal.ticker) ||
                toNonEmptyString(proposal.name) ||
                `Ticker ${tickerId}`;
              const entryForPrompt = formatPromptEntryPrice(
                proposal.entryPrice,
                proposal.assetType,
              );
              const tradeSuffix = tradeSide && entryForPrompt
                ? `; ${tradeSide} at ${entryForPrompt}`
                : entryForPrompt
                  ? `; at ${entryForPrompt}`
                  : "";
              onQuestionSubmit(
                `Analyze current ${labelTicker} trade action${tradeSuffix}`,
                null,
                [tickerId],
                [],
                { tradeProposalId: proposal.id },
              );
              if (typeof setActivePanel === "function") {
                setActivePanel("chat");
              }
            }}
          >
            Analyze Trade
          </Box>
          <Box
            className="base-text"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 0.6,
              borderRadius: "999px",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: isStoppingTracking
                ? tradePanelColors.subtext
                : tradePanelColors.text,
              backgroundColor: tradePanelColors.itemHover,
              border: `1px solid ${tradePanelColors.divider}`,
              cursor: isStoppingTracking ? "default" : "pointer",
              userSelect: "none",
              opacity: isStoppingTracking ? 0.7 : 1,
              "&:hover": isStoppingTracking
                ? {}
                : {
                    borderColor: tradePanelColors.highlightBorder,
                    backgroundColor: tradePanelColors.highlightBg,
                  },
            }}
            onClick={() => {
              if (isStoppingTracking) return;
              onStopTrackingTrade(proposal);
            }}
          >
            {isStoppingTracking ? "Stopping..." : "Stop Tracking"}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
